import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AnalyticsCockpitLayout,
  AnalyticsDashboardInput,
  AnalyticsGoalInput,
  AnalyticsPrefsInput,
  AnalyticsQueryFilters,
  AnalyticsQueryRequest,
  AnalyticsReportInput,
  AnalyticsGroupBy,
} from "@sales-ops-amplify/shared";
import {
  createAnalyticsDashboard,
  createAnalyticsGoal,
  createAnalyticsReport,
  getAnalyticsCatalog,
  getAnalyticsCockpit,
  getAnalyticsDashboard,
  getAnalyticsPreferences,
  getAnalyticsSummary,
  listAnalyticsDashboards,
  listAnalyticsGoals,
  listAnalyticsReports,
  queryAnalytics,
  runAnalyticsReport,
  setDefaultAnalyticsDashboard,
  updateAnalyticsDashboard,
  updateAnalyticsGoal,
  updateAnalyticsPreferences,
} from "@/api/analytics";

export function useAnalyticsSummary() {
  return useQuery({
    queryKey: ["analytics", "summary"],
    queryFn: getAnalyticsSummary,
    staleTime: 30_000,
  });
}

export function useAnalyticsCatalog() {
  return useQuery({
    queryKey: ["analytics", "catalog"],
    queryFn: getAnalyticsCatalog,
    staleTime: 60_000,
  });
}

export function useAnalyticsPreferences() {
  return useQuery({
    queryKey: ["analytics", "preferences"],
    queryFn: getAnalyticsPreferences,
  });
}

export function useAnalyticsQuery(
  request: AnalyticsQueryRequest | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ["analytics", "query", request],
    queryFn: () => queryAnalytics(request!),
    enabled: enabled && Boolean(request?.metrics?.length),
    staleTime: 15_000,
  });
}

export function useAnalyticsDashboards() {
  return useQuery({
    queryKey: ["analytics", "dashboards"],
    queryFn: listAnalyticsDashboards,
  });
}

export function useAnalyticsDashboard(id: string | undefined) {
  return useQuery({
    queryKey: ["analytics", "dashboard", id],
    queryFn: () => getAnalyticsDashboard(id!),
    enabled: Boolean(id),
  });
}

export function useAnalyticsReports() {
  return useQuery({
    queryKey: ["analytics", "reports"],
    queryFn: listAnalyticsReports,
  });
}

export function useRunAnalyticsReport(
  id: string | undefined,
  opts?: { filters?: AnalyticsQueryFilters; groupBy?: AnalyticsGroupBy },
) {
  return useQuery({
    queryKey: ["analytics", "report-run", id, opts],
    queryFn: () => runAnalyticsReport(id!, opts),
    enabled: Boolean(id),
  });
}

export function useAnalyticsGoals() {
  return useQuery({
    queryKey: ["analytics", "goals"],
    queryFn: listAnalyticsGoals,
  });
}

export function useAnalyticsCockpit(layout?: AnalyticsCockpitLayout) {
  return useQuery({
    queryKey: ["analytics", "cockpit", layout],
    queryFn: () => getAnalyticsCockpit(layout),
  });
}

export function useUpdateAnalyticsPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AnalyticsPrefsInput) =>
      updateAnalyticsPreferences(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["analytics", "preferences"] });
    },
  });
}

export function useCreateAnalyticsDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AnalyticsDashboardInput) =>
      createAnalyticsDashboard(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["analytics", "dashboards"] });
    },
  });
}

export function useUpdateAnalyticsDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<AnalyticsDashboardInput> & { favorite?: boolean };
    }) => updateAnalyticsDashboard(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["analytics", "dashboards"] });
      qc.invalidateQueries({ queryKey: ["analytics", "preferences"] });
    },
  });
}

export function useSetDefaultDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => setDefaultAnalyticsDashboard(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["analytics", "preferences"] });
    },
  });
}

export function useCreateAnalyticsReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AnalyticsReportInput) => createAnalyticsReport(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["analytics", "reports"] });
    },
  });
}

export function useCreateAnalyticsGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AnalyticsGoalInput) => createAnalyticsGoal(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["analytics", "goals"] });
    },
  });
}

export function useUpdateAnalyticsGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<AnalyticsGoalInput>;
    }) => updateAnalyticsGoal(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["analytics", "goals"] });
    },
  });
}
